import type {
  ParameterCategory,
  ParameterDefinition,
  ParameterChange,
  ParameterSavePayload,
} from './ParameterEditor.types';

/**
 * Strips diacritics (tildes, accents) for accent-insensitive comparison.
 * Only used at comparison time — never mutates the original data.
 */
function stripDiacritics(text: string): string {
  return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

/**
 * Returns all parameters from a category (direct + from sections).
 */
export function getAllCategoryParameters(
  category: ParameterCategory
): ParameterDefinition[] {
  const direct = category.parameters ?? [];
  const fromSections = (category.sections ?? []).flatMap((s) => s.parameters);
  return [...direct, ...fromSections];
}

/**
 * Builds the initial values map from dataSource.
 * Maps parameterId -> resolvedValue
 */
export function buildOriginalValues(
  dataSource: ParameterCategory[]
): Map<string, string> {
  const map = new Map<string, string>();
  for (const cat of dataSource) {
    for (const param of getAllCategoryParameters(cat)) {
      map.set(param.id, param.resolvedValue);
    }
  }
  return map;
}

/**
 * Validates a parameter value against its validation rules.
 * Returns an error message if invalid, or empty string if valid.
 */
export function validateParameter(
  param: ParameterDefinition,
  value: string
): string {
  if (!param.validations || param.validations.length === 0) return '';

  for (const rule of param.validations) {
    switch (rule.ruleType) {
      case 'REGEX':
      case 'FORMAT': {
        try {
          const regex = new RegExp(rule.ruleValue);
          if (!regex.test(value)) return rule.errorMessage;
        } catch {
          // Invalid regex, skip validation
        }
        break;
      }
      case 'MIN': {
        const numVal = parseFloat(value);
        const minVal = parseFloat(rule.ruleValue);
        if (!isNaN(numVal) && !isNaN(minVal) && numVal < minVal) {
          return rule.errorMessage;
        }
        break;
      }
      case 'MAX': {
        const numVal = parseFloat(value);
        const maxVal = parseFloat(rule.ruleValue);
        if (!isNaN(numVal) && !isNaN(maxVal) && numVal > maxVal) {
          return rule.errorMessage;
        }
        break;
      }
      case 'MAX_LENGTH': {
        const maxLen = parseInt(rule.ruleValue, 10);
        if (!isNaN(maxLen) && value.length > maxLen) {
          return rule.errorMessage;
        }
        break;
      }
    }
  }

  return '';
}

/**
 * Filters parameters by search term.
 * Matches against name, code, and description (case-insensitive).
 */
export function filterParameters(
  parameters: ParameterDefinition[],
  searchTerm: string
): ParameterDefinition[] {
  if (!searchTerm.trim()) return parameters;
  const term = stripDiacritics(searchTerm.toLowerCase());
  return parameters.filter(
    (p) =>
      stripDiacritics(p.name.toLowerCase()).includes(term) ||
      stripDiacritics(p.code.toLowerCase()).includes(term) ||
      stripDiacritics(p.description.toLowerCase()).includes(term)
  );
}

/**
 * Returns visible parameters sorted by sortOrder.
 */
export function getVisibleParameters(
  parameters: ParameterDefinition[]
): ParameterDefinition[] {
  return parameters
    .filter((p) => p.isVisible !== false)
    .sort((a, b) => a.sortOrder - b.sortOrder);
}

/**
 * Gets the count of changes for a specific category.
 */
export function getCategoryChangeCount(
  category: ParameterCategory,
  editedValues: Map<string, string>,
  originalValues: Map<string, string>
): number {
  let count = 0;
  for (const param of getAllCategoryParameters(category)) {
    const edited = editedValues.get(param.id);
    const original = originalValues.get(param.id);
    if (edited !== undefined && edited !== original) {
      count++;
    }
  }
  return count;
}

/**
 * Gets the total count of all changes across dataSource.
 */
export function getTotalChangeCount(
  editedValues: Map<string, string>,
  originalValues: Map<string, string>
): number {
  let count = 0;
  for (const [id, value] of editedValues) {
    if (value !== originalValues.get(id)) {
      count++;
    }
  }
  return count;
}

/**
 * Builds the list of changes for the confirm panel.
 */
export function buildChangeList(
  dataSource: ParameterCategory[],
  editedValues: Map<string, string>,
  originalValues: Map<string, string>,
  entityDisplayLabels?: Map<string, string>
): ParameterChange[] {
  const changes: ParameterChange[] = [];

  const pushChange = (
    param: ParameterDefinition,
    catName: string,
    catId: string,
    sectionName?: string,
    sectionId?: string
  ) => {
    const edited = editedValues.get(param.id);
    const original = originalValues.get(param.id);
    if (edited !== undefined && edited !== original) {
      changes.push({
        parameterId: param.id,
        parameterCode: param.code,
        parameterName: param.name,
        categoryName: catName,
        categoryId: catId,
        sectionName,
        sectionId,
        dataType: param.dataType,
        oldValue: original ?? '',
        newValue: edited,
        isSensitive: param.isSensitive,
        listOptions: param.listOptions,
        oldEntityDisplayLabel: entityDisplayLabels?.get(original ?? ''),
        newEntityDisplayLabel: entityDisplayLabels?.get(edited),
        rowVersion: param.rowVersion,
      });
    }
  };

  for (const cat of dataSource) {
    for (const param of cat.parameters ?? []) {
      pushChange(param, cat.name, cat.id);
    }
    for (const section of cat.sections ?? []) {
      for (const param of section.parameters) {
        pushChange(param, cat.name, cat.id, section.name, section.id);
      }
    }
  }

  return changes;
}

/**
 * Filters a category by search term, including sections.
 * Returns null if no parameters match.
 */
export function filterCategoryBySearch(
  category: ParameterCategory,
  searchTerm: string
): ParameterCategory | null {
  if (!searchTerm.trim()) return category;

  const filteredParams = filterParameters(category.parameters ?? [], searchTerm);
  const filteredSections = (category.sections ?? [])
    .map((section) => ({
      ...section,
      parameters: filterParameters(section.parameters, searchTerm),
    }))
    .filter((section) => section.parameters.length > 0);

  if (filteredParams.length === 0 && filteredSections.length === 0) return null;

  return {
    ...category,
    parameters: filteredParams,
    sections: filteredSections,
  };
}

/**
 * Builds the save payload from the change list.
 */
export function buildSavePayload(
  changes: ParameterChange[]
): ParameterSavePayload[] {
  return changes.map((c) => ({
    parameterId: c.parameterId,
    parameterCode: c.parameterCode,
    value: c.newValue,
    rowVersion: c.rowVersion,
  }));
}

/**
 * Returns the display label for a value.
 * For BOOLEAN returns "Sí"/"No", for LIST returns the option label, for sensitive returns masked.
 */
export function getDisplayValue(
  value: string,
  dataType: string,
  isSensitive: boolean,
  listOptions?: Array<{ value: string; label: string }> | null,
  entityDisplayLabel?: string
): string {
  if (isSensitive) return '••••••••';

  if (dataType === 'BOOLEAN') {
    return value === 'true' ? 'Sí' : 'No';
  }

  if (dataType === 'LIST' && listOptions) {
    const option = listOptions.find((o) => String(o.value) === String(value));
    return option ? option.label : value;
  }

  if (dataType === 'ENTITY' && entityDisplayLabel) {
    return entityDisplayLabel;
  }

  return value;
}

/**
 * Gets the current value for a parameter (edited or original).
 */
export function getCurrentValue(
  paramId: string,
  editedValues: Map<string, string>,
  originalValues: Map<string, string>
): string {
  return editedValues.has(paramId)
    ? editedValues.get(paramId)!
    : originalValues.get(paramId) ?? '';
}

/**
 * Extracts min/max values from validation rules.
 */
export function getMinMaxFromValidations(
  validations: ParameterDefinition['validations']
): { min?: number; max?: number; maxLength?: number } {
  const result: { min?: number; max?: number; maxLength?: number } = {};

  for (const rule of validations) {
    if (rule.ruleType === 'MIN') {
      result.min = parseFloat(rule.ruleValue);
    } else if (rule.ruleType === 'MAX') {
      result.max = parseFloat(rule.ruleValue);
    } else if (rule.ruleType === 'MAX_LENGTH') {
      result.maxLength = parseInt(rule.ruleValue, 10);
    }
  }

  return result;
}
